import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiLayers,
  FiUsers,
  FiPlus,
  FiShield,
  FiGlobe,
  FiCheckCircle,
} from "react-icons/fi";
import { BASE_URL } from "./baseUrl";

const defaultOrgForm = {
  name: "",
  slug: "",
  allowedDomains: "",
  maxUsers: 50,
};

const defaultAdminForm = {
  name: "",
  email: "",
  password: "",
  phone: "",
  jobTitle: "",
};

export default function Organizations() {
  const [organizations, setOrganizations] = useState([]);
  const [orgForm, setOrgForm] = useState(defaultOrgForm);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [adminForm, setAdminForm] = useState(defaultAdminForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = localStorage.getItem("token");

  async function loadOrganizations() {
    try {
      const res = await axios.get(`${BASE_URL}/platform/organizations`, {
        headers: { authorization: `Bearer ${token}` },
      });
      setOrganizations(res.data.organizations || []);
    } catch (err) {
      console.error("loadOrganizations error", err?.response || err);
      setError(err?.response?.data?.error || "Failed to load organizations");
    }
  }

  async function createOrganization(e) {
    e.preventDefault();
    try {
      setError("");
      setSuccess("");

      await axios.post(
        `${BASE_URL}/platform/organizations`,
        {
          ...orgForm,
          allowedDomains: orgForm.allowedDomains
            .split(",")
            .map((x) => x.trim().toLowerCase())
            .filter(Boolean),
        },
        { headers: { authorization: `Bearer ${token}` } }
      );

      setOrgForm(defaultOrgForm);
      setSuccess("Organization created successfully");
      await loadOrganizations();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create organization");
    }
  }

  async function createOrgAdmin(e) {
    e.preventDefault();
    if (!selectedOrgId) {
      setError("Please select an organization first");
      return;
    }

    try {
      setError("");
      setSuccess("");

      await axios.post(
        `${BASE_URL}/platform/organizations/${selectedOrgId}/admins`,
        adminForm,
        { headers: { authorization: `Bearer ${token}` } }
      );

      setAdminForm(defaultAdminForm);
      setSuccess("Organization admin created successfully");
      await loadOrganizations();
    } catch (err) {
      setError(
        err?.response?.data?.error || "Failed to create organization admin"
      );
    }
  }

  useEffect(() => {
    loadOrganizations();
  }, []);

  const summary = useMemo(
    () => ({
      total: organizations.length,
      active: organizations.filter((org) => org.status === "active").length,
      suspended: organizations.filter((org) => org.status === "suspended").length,
    }),
    [organizations]
  );

  return (
    <div className="space-y-8">
      <section className="rounded-[32px] border border-white/10 bg-white/5 p-6 shadow-xl shadow-black/10 backdrop-blur-sm lg:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex rounded-full border border-violet-400/20 bg-violet-400/10 px-4 py-2 text-sm font-medium text-violet-200">
              Multi-tenant workspace control
            </div>

            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-white lg:text-4xl">
              Organizations
            </h1>

            <p className="mt-3 max-w-2xl text-slate-300">
              Create client workspaces, assign organization admins, and manage
              tenant access across the platform.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-3xl border border-white/10 bg-slate-950/40 p-5">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 text-violet-200">
              <FiLayers className="text-lg" />
            </div>
            <div className="mt-4 text-sm text-slate-400">Total organizations</div>
            <div className="mt-1 text-2xl font-semibold text-white">
              {summary.total}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-slate-950/40 p-5">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 text-emerald-200">
              <FiCheckCircle className="text-lg" />
            </div>
            <div className="mt-4 text-sm text-slate-400">Active</div>
            <div className="mt-1 text-2xl font-semibold text-white">
              {summary.active}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-slate-950/40 p-5">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/20 to-cyan-500/20 text-sky-200">
              <FiUsers className="text-lg" />
            </div>
            <div className="mt-4 text-sm text-slate-400">Suspended</div>
            <div className="mt-1 text-2xl font-semibold text-white">
              {summary.suspended}
            </div>
          </div>
        </div>
      </section>

      {error ? (
        <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-red-200">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-emerald-200">
          {success}
        </div>
      ) : null}

      <section className="grid gap-6 xl:grid-cols-2">
        <form
          onSubmit={createOrganization}
          className="rounded-[32px] border border-white/10 bg-white/5 p-6 shadow-xl shadow-black/10 backdrop-blur-sm"
        >
          <div className="mb-6 flex items-start gap-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 text-violet-200">
              <FiPlus className="text-xl" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white">
                Create organization
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                Add a new company workspace.
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200">
                Organization name
              </label>
              <input
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
                placeholder="E-Lex"
                value={orgForm.name}
                onChange={(e) =>
                  setOrgForm({ ...orgForm, name: e.target.value })
                }
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200">
                Slug
              </label>
              <input
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
                placeholder="elex"
                value={orgForm.slug}
                onChange={(e) =>
                  setOrgForm({ ...orgForm, slug: e.target.value })
                }
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200">
                Allowed domains
              </label>
              <input
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
                placeholder="elexsignature.gm, example.com"
                value={orgForm.allowedDomains}
                onChange={(e) =>
                  setOrgForm({
                    ...orgForm,
                    allowedDomains: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200">
                Max users
              </label>
              <input
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
                type="number"
                value={orgForm.maxUsers}
                onChange={(e) =>
                  setOrgForm({
                    ...orgForm,
                    maxUsers: Number(e.target.value),
                  })
                }
              />
            </div>
          </div>

          <button className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-violet-500 via-fuchsia-500 to-indigo-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-900/30 transition hover:scale-[1.01]">
            <FiPlus />
            Create organization
          </button>
        </form>

        <form
          onSubmit={createOrgAdmin}
          className="rounded-[32px] border border-white/10 bg-white/5 p-6 shadow-xl shadow-black/10 backdrop-blur-sm"
        >
          <div className="mb-6 flex items-start gap-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/20 to-cyan-500/20 text-sky-200">
              <FiShield className="text-xl" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white">
                Create first org admin
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                Assign the first admin for a selected workspace.
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200">
                Organization
              </label>
              <select
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                <option className="bg-slate-900" value="">
                  Select organization
                </option>
                {organizations.map((org) => (
                  <option
                    className="bg-slate-900"
                    key={org._id}
                    value={org._id}
                  >
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            <input
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
              placeholder="Full name"
              value={adminForm.name}
              onChange={(e) =>
                setAdminForm({ ...adminForm, name: e.target.value })
              }
            />
            <input
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
              placeholder="Email"
              value={adminForm.email}
              onChange={(e) =>
                setAdminForm({ ...adminForm, email: e.target.value })
              }
            />
            <input
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
              type="password"
              placeholder="Temporary password"
              value={adminForm.password}
              onChange={(e) =>
                setAdminForm({ ...adminForm, password: e.target.value })
              }
            />
            <input
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
              placeholder="Phone"
              value={adminForm.phone}
              onChange={(e) =>
                setAdminForm({ ...adminForm, phone: e.target.value })
              }
            />
            <input
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none placeholder:text-slate-500 focus:border-violet-400/50 focus:ring-4 focus:ring-violet-500/10"
              placeholder="Job title"
              value={adminForm.jobTitle}
              onChange={(e) =>
                setAdminForm({ ...adminForm, jobTitle: e.target.value })
              }
            />
          </div>

          <button className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 to-cyan-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-900/30 transition hover:scale-[1.01]">
            <FiShield />
            Create org admin
          </button>
        </form>
      </section>

      <section className="rounded-[32px] border border-white/10 bg-white/5 p-6 shadow-xl shadow-black/10 backdrop-blur-sm">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-white">
              Existing organizations
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              Review tenant workspaces and current status.
            </p>
          </div>

          <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-300">
            {organizations.length} total
          </div>
        </div>

        {organizations.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-slate-950/30 p-8 text-center text-slate-400">
            No organizations found.
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {organizations.map((org) => (
              <div
                key={org._id}
                className="rounded-3xl border border-white/10 bg-slate-950/40 p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xl font-semibold text-white">
                      {org.name}
                    </div>
                    <div className="mt-1 text-sm text-slate-400">
                      Slug: {org.slug}
                    </div>
                  </div>

                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] ${
                      org.status === "active"
                        ? "border border-emerald-400/20 bg-emerald-500/10 text-emerald-200"
                        : "border border-amber-400/20 bg-amber-500/10 text-amber-200"
                    }`}
                  >
                    {org.status}
                  </span>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <FiGlobe />
                      Domain policy
                    </div>
                    <div className="mt-2 text-sm text-slate-200">
                      {org.allowedDomains?.length
                        ? org.allowedDomains.join(", ")
                        : "No domain restriction"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <FiUsers />
                      User limit
                    </div>
                    <div className="mt-2 text-sm text-slate-200">
                      {org.limits?.maxUsers || 50} users
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}